/**
 * Strategy static config — Rive timeouts, risk profile copy, review summaries.
 *
 * Belongs here: display config.
 * Does not belong here: flow rules.
 */
/**
 * Strategy questionnaire — static config (Rives, timeouts, risk profile UI, review copy).
 * No React. Safe to import from screens and review.
 */
import type {
  StrategyQuestionnaireStage,
  StrategyQuestionnaireAnswersMap,
  ReviewSummaryStage
} from '../../shared/types/questionnaire.types'
import { getVisibleQuestions } from '../../question-utils/visibleQuestions'
import { evaluateCondition } from '../../question-utils/evaluateCondition'
import { formatRupeeLabel } from '../../../plans-v4/display-values/lumpsumPlanAmount'
import { AllocationRiskProfile } from '../../types/allocation.types'

export { getStageIntroRiveSrc, STAGE_COMPLETION_RIVE_SRC } from './stageAssets'

export const STAGE_INTRO_RIVE_TIMEOUT_MS = 10000
export const STAGE_COMPLETION_RIVE_TIMEOUT_MS = 5000

/** Last-question leave CSS duration (Q→Q stays on ~440ms). */
export const STAGE_COMPLETE_QUESTIONS_EXIT_OUT_MS = 900

/**
 * Delay after leave starts before mounting stage-complete Rive.
 * Lower than EXIT_OUT to overlap; equal to wait for full swipe; higher adds a gap.
 */
export const STAGE_COMPLETE_RIVE_START_AFTER_MS = 250

// ─── Risk profile screen ─────────────────────────────────────────────────────

export interface AllocationRiskProfileConfig {
  /** Display title, e.g. "Conservative". */
  name: string
  description: string
  /** Hero illustration under /public. */
  assetSrc: string
}

const BASE_RISK_PROFILE_ASSET = '/images/allocation/risk-profiles-v2/allocation'

export const ALLOCATION_RISK_PROFILE_CONFIG: Record<AllocationRiskProfile, AllocationRiskProfileConfig> = {
  [AllocationRiskProfile.CONSERVATIVE]: {
    name: 'Conservative',
    description:
      '<span style="font-weight:500;color:rgba(0,0,0,0.7);">Built to grow steadily with low risks. </span>' +
      '<span style="font-weight:400;color:rgba(0,0,0,0.7);">Ideal when your goals are closer, or when stability matters more than chasing returns.</span>',
    assetSrc: `${BASE_RISK_PROFILE_ASSET}/allocation-conservative.png`
  },
  [AllocationRiskProfile.BALANCED]: {
    name: 'Balanced',
    description:
      '<span style="font-weight:500;color:rgba(0,0,0,0.7);">Built to grow wealth by keeping risks within comfortable limits. </span>' +
      '<span style="font-weight:400;color:rgba(0,0,0,0.7);">Ideal when returns matter, but protecting capital matters equally.</span>',
    assetSrc: `${BASE_RISK_PROFILE_ASSET}/allocation-balanced.png`
  },
  [AllocationRiskProfile.AGGRESSIVE]: {
    name: 'Aggressive',
    description:
      '<span style="font-weight:500;color:rgba(0,0,0,0.7);">Built for growing wealth by taking high risks. </span>' +
      '<span style="font-weight:400;color:rgba(0,0,0,0.7);">Ideal when you have a long time horizon, so you can stay invested and let wealth compound.</span>',
    assetSrc: `${BASE_RISK_PROFILE_ASSET}/allocation-aggressive.png`
  },
  [AllocationRiskProfile.VERY_AGGRESSIVE]: {
    name: 'Very Aggressive',
    description:
      '<span style="font-weight:500;color:rgba(0,0,0,0.7);">Built to maximise growth with bigger risks. </span>' +
      '<span style="font-weight:400;color:rgba(0,0,0,0.7);">Ideal when you have a long time horizon and major market falls won\'t affect you.</span>',
    assetSrc: `${BASE_RISK_PROFILE_ASSET}/allocation-very-aggressive.png`
  }
}

const DEFAULT_RISK_PROFILE_CONFIG = ALLOCATION_RISK_PROFILE_CONFIG[AllocationRiskProfile.AGGRESSIVE]

export function getAllocationRiskProfileConfig(
  riskProfile: AllocationRiskProfile | null | undefined
): AllocationRiskProfileConfig {
  if (!riskProfile) return DEFAULT_RISK_PROFILE_CONFIG
  return ALLOCATION_RISK_PROFILE_CONFIG[riskProfile] ?? DEFAULT_RISK_PROFILE_CONFIG
}

// ─── Review summary lines ────────────────────────────────────────────────────

function formatReviewCopyValue(value: string | number | string[] | undefined): string {
  if (value == null) return ''
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatRupeeLabel({ rawAmount: value })
  }
  if (Array.isArray(value)) return formatReviewCopyValue(value[0])
  return String(value)
}

export function getStageSummaryLines(
  stage: StrategyQuestionnaireStage,
  answers: StrategyQuestionnaireAnswersMap,
  reviewSummary?: ReviewSummaryStage[] | null
): string[] {
  const stageSummary = reviewSummary?.find((summaryStage) => summaryStage.stageId === stage.id)
  if (stageSummary?.reviewCopies?.length) {
    const lines: string[] = []
    const answersMap = answers ?? {}
    for (const reviewCopy of stageSummary.reviewCopies) {
      for (const evaluation of reviewCopy.copyEvaluations ?? []) {
        if (!evaluateCondition(evaluation.conditionalExpression, answersMap)) continue
        for (const copy of evaluation.copies ?? []) {
          let text = copy.displayText ?? ''
          const placeHolders = copy.placeHolders ?? []
          if (placeHolders.length > 0 && text.includes('%s')) {
            const values = placeHolders.map((placeHolder) => formatReviewCopyValue(answersMap[placeHolder.questionId]))
            text = text.replace(/%s/g, () => values.shift() ?? '')
          }
          if (text.trim()) lines.push(text.trim())
        }
        break
      }
    }
    return lines
  }

  const visible = getVisibleQuestions(stage, answers)
  const lines: string[] = []
  for (const question of visible) {
    const value = question?.id != null ? answers[question.id] : undefined
    if (value == null) continue
    if (typeof value === 'number') {
      lines.push(formatRupeeLabel({ rawAmount: value }))
      continue
    }
    const optionIds = Array.isArray(value) ? value : [value]
    const texts = optionIds
      .map((optionId) => (question.options ?? []).find((option) => option.id === optionId)?.displayText)
      .filter(Boolean) as string[]
    if (texts.length > 0) lines.push(texts.join(', '))
  }
  return lines
}
